/**
 * DemoStage — shared framed workspace and exercise guide for interactive demos.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { cva } from "class-variance-authority";
import { CheckCircle2, ListChecks, PlayCircle } from "lucide-react";
import { FONT, GRAY, PRIMARY, SAFE, WARN } from "../design-system/index.js";
import { cn } from "../styling/index.js";

export type DemoStageTone = "neutral" | "safe" | "danger" | "info";

export interface DemoStageProps {
  readonly title: React.ReactNode;
  readonly children: React.ReactNode;
  readonly accent?: string;
  readonly ariaLabel?: string;
  readonly className?: string;
  readonly description?: React.ReactNode;
  readonly eyebrow?: React.ReactNode;
  readonly footer?: React.ReactNode;
  readonly icon?: React.ReactNode;
  readonly style?: React.CSSProperties;
  readonly tone?: DemoStageTone;
}

export interface ExerciseGuideProps {
  readonly steps: readonly React.ReactNode[];
  readonly accent?: string;
  readonly className?: string;
  readonly description?: React.ReactNode;
  readonly style?: React.CSSProperties;
  readonly title?: React.ReactNode;
  readonly tone?: DemoStageTone;
}

interface TonePalette {
  readonly accent: string;
  readonly background: string;
  readonly border: string;
  readonly iconBackground: string;
}

const stageClass = cva("csk-demo-stage", {
  variants: {
    tone: {
      danger: "csk-demo-stage--danger",
      info: "csk-demo-stage--info",
      neutral: "csk-demo-stage--neutral",
      safe: "csk-demo-stage--safe",
    },
  },
  defaultVariants: { tone: "neutral" },
});

function alpha(color: string, alphaHex: string): string {
  return /^#[0-9a-f]{6}$/i.test(color) ? `${color}${alphaHex}` : color;
}

function paletteFor(tone: DemoStageTone, accentOverride: string | undefined): TonePalette {
  const accent =
    accentOverride ??
    {
      danger: WARN[500],
      info: PRIMARY[600],
      neutral: GRAY[700],
      safe: SAFE[500],
    }[tone];
  return {
    accent,
    background: `linear-gradient(135deg, ${alpha(accent, "12")} 0%, #ffffff 54%, ${GRAY[50]} 100%)`,
    border: alpha(accent, "44"),
    iconBackground: alpha(accent, "14"),
  };
}

export const DemoStage = React.memo(function DemoStage({
  title,
  children,
  accent,
  ariaLabel,
  className,
  description,
  eyebrow,
  footer,
  icon,
  style,
  tone = "neutral",
}: DemoStageProps): React.JSX.Element {
  const palette = paletteFor(tone, accent);
  const titleId = `demo-stage-title-${React.useId().replace(/[^A-Za-z0-9_-]+/g, "")}`;
  return (
    <section
      aria-label={ariaLabel}
      aria-labelledby={ariaLabel === undefined ? titleId : undefined}
      className={cn(stageClass({ tone }), className)}
      style={{
        background: palette.background,
        border: `1px solid ${GRAY[300]}`,
        borderLeft: `4px solid ${palette.accent}`,
        borderRadius: 10,
        boxShadow: `0 14px 32px ${alpha(GRAY[900], "12")}, inset 0 0 0 1px ${alpha(
          palette.accent,
          "10",
        )}`,
        marginTop: "1rem",
        overflow: "hidden",
        ...style,
      }}
    >
      <div
        style={{
          alignItems: "flex-start",
          borderBottom: `1px solid ${palette.border}`,
          display: "flex",
          gap: "0.85rem",
          padding: "1rem 1.1rem",
        }}
      >
        <span
          aria-hidden="true"
          style={{
            alignItems: "center",
            background: palette.iconBackground,
            border: `1px solid ${palette.border}`,
            borderRadius: 8,
            color: palette.accent,
            display: "inline-flex",
            flexShrink: 0,
            height: 34,
            justifyContent: "center",
            marginTop: 1,
            width: 34,
          }}
        >
          {icon ?? <PlayCircle size={18} />}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          {eyebrow !== undefined ? (
            <div
              style={{
                color: palette.accent,
                fontFamily: FONT.mono,
                fontSize: "0.72rem",
                fontWeight: 800,
                letterSpacing: "0.08em",
                marginBottom: "0.25rem",
                textTransform: "uppercase",
              }}
            >
              {eyebrow}
            </div>
          ) : null}
          <div id={titleId} style={{ color: GRAY[900], fontSize: "1rem", fontWeight: 800 }}>
            {title}
          </div>
          {description !== undefined ? (
            <div style={{ color: GRAY[600], fontSize: "0.875rem", lineHeight: 1.55, marginTop: 4 }}>
              {description}
            </div>
          ) : null}
        </div>
      </div>

      <div style={{ padding: "1.1rem" }}>{children}</div>
      {footer !== undefined ? (
        <div
          style={{
            background: alpha(palette.accent, "0a"),
            borderTop: `1px solid ${palette.border}`,
            color: GRAY[700],
            fontSize: "0.8125rem",
            padding: "0.8rem 1.1rem",
          }}
        >
          {footer}
        </div>
      ) : null}
    </section>
  );
});

export const ExerciseGuide = React.memo(function ExerciseGuide({
  steps,
  accent,
  className,
  description,
  style,
  title = "How to test",
  tone = "info",
}: ExerciseGuideProps): React.JSX.Element {
  const palette = paletteFor(tone, accent);
  return (
    <section
      aria-label={typeof title === "string" ? title : "Exercise guide"}
      className={cn("csk-exercise-guide", className)}
      style={{
        background: `linear-gradient(135deg, ${alpha(palette.accent, "10")}, ${GRAY[50]})`,
        border: `1px solid ${palette.border}`,
        borderRadius: 10,
        marginTop: "1.5rem",
        padding: "1rem 1.1rem",
        ...style,
      }}
    >
      <div style={{ alignItems: "center", display: "flex", gap: "0.65rem" }}>
        <span
          aria-hidden="true"
          style={{
            alignItems: "center",
            background: "#ffffff",
            border: `1px solid ${palette.border}`,
            borderRadius: 8,
            color: palette.accent,
            display: "inline-flex",
            height: 30,
            justifyContent: "center",
            width: 30,
          }}
        >
          <ListChecks size={17} />
        </span>
        <div>
          <div style={{ color: GRAY[900], fontSize: "0.95rem", fontWeight: 800 }}>{title}</div>
          {description !== undefined ? (
            <div style={{ color: GRAY[600], fontSize: "0.82rem", lineHeight: 1.45 }}>
              {description}
            </div>
          ) : null}
        </div>
      </div>
      <ol style={{ display: "grid", gap: "0.55rem", margin: "0.85rem 0 0", padding: 0 }}>
        {steps.map((step, index) => (
          <li
            key={index}
            style={{
              alignItems: "flex-start",
              background: "#ffffff",
              border: `1px solid ${GRAY[300]}`,
              borderRadius: 8,
              display: "grid",
              gap: "0.55rem",
              gridTemplateColumns: "22px minmax(0, 1fr)",
              listStyle: "none",
              padding: "0.65rem 0.75rem",
            }}
          >
            <CheckCircle2 aria-hidden="true" color={palette.accent} size={18} />
            <span style={{ color: GRAY[700], fontSize: "0.875rem", lineHeight: 1.5 }}>{step}</span>
          </li>
        ))}
      </ol>
    </section>
  );
});
